import { useEffect, useState, useCallback, useContext } from "react";
import { WEBSITE_TUNNEL_TYPES } from "app/constants";
import { v4 as uuidv4 } from "uuid";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import AppGlobalsContext from "app/AppGlobalsContext";

const STORAGE_KEY = "pfs:searchHistory";
const MAX_HISTORY_LENGTH = 5;

const INITIAL_STATE = {
	[WEBSITE_TUNNEL_TYPES.HOLIDAYS]: [],
	[WEBSITE_TUNNEL_TYPES.STOPOVER]: [],
	[WEBSITE_TUNNEL_TYPES.HOTEL_ONLY]: [],
};

const filterEntryToDisplay = searchHistory => {
	const today = new Date();
	return searchHistory.filter(searchEntry => {
		// check if searchEntry.data.departureDate timestamp to before today
		if (searchEntry?.data?.departureDate) {
			const departureDate = new Date(searchEntry?.data?.departureDate);
			return departureDate >= today;
		}
		return true;
	});
};

const validateEntryToSave = (searchEntry, existingCategoryHistoryEntries) => {
	// check if searchEntry.searchQueryString is equal to last existingCategoryHistoryEntries searchQueryString
	if (
		existingCategoryHistoryEntries.length > 0 &&
		existingCategoryHistoryEntries[existingCategoryHistoryEntries.length - 1]
			.searchQueryString === searchEntry.searchQueryString
	) {
		return false; // Do not save if the last entry is the same
	}

	return true;
};

const useSearchHistory = category => {
	const [historyMap, setHistoryMap] = useState(INITIAL_STATE);

	const { shop } = useContext(AppGlobalsContext);

	const storageKey = `${STORAGE_KEY}:${shop}`;

	const { track } = useAnalytics();

	// Load from localStorage on mount
	useEffect(() => {
		const stored = localStorage?.getItem(storageKey);
		if (stored) {
			try {
				const parsed = JSON.parse(stored);
				const filteredParsed = Object.keys(parsed).reduce((acc, key) => {
					acc[key] = filterEntryToDisplay(parsed[key]);
					return acc;
				}, {});
				setHistoryMap({ ...INITIAL_STATE, ...filteredParsed });

				const isAnyHistoryAvailable = Object.values(filteredParsed).some(
					entries => entries.length > 0
				);

				if (isAnyHistoryAvailable) {
					track("last-searches-available");
				}

				// last-searches-available
			} catch (err) {
				track("use-search-history-read-error", { error: err?.message });
			}
		}
	}, [storageKey]);

	// Add a new search to the correct category
	const addSearch = useCallback(
		(searchEntry, searchQueryString) => {
			const uuid = uuidv4();
			const newEntry = { data: searchEntry, id: uuid, searchQueryString };
			setHistoryMap(prevMap => {
				const existingCategoryHistoryEntries = prevMap[category] || [];

				const isValidEntry = validateEntryToSave(newEntry, existingCategoryHistoryEntries);

				if (isValidEntry) {
					const updatedCategoryHistory = [
						...existingCategoryHistoryEntries,
						newEntry,
					].slice(-MAX_HISTORY_LENGTH);
					const updatedMap = {
						...prevMap,
						[category]: updatedCategoryHistory,
					};
					localStorage.setItem(storageKey, JSON.stringify(updatedMap));
					return updatedMap;
				}
				return existingCategoryHistoryEntries;
			});
		},
		[category, storageKey]
	);

	const getSearchSearchById = useCallback(
		id => {
			let searchHistory;
			const categoryHistory = historyMap[category] || [];
			searchHistory = categoryHistory.find(entry => entry?.id === id);

			return searchHistory;
		},
		[category, historyMap]
	);

	const removeSearch = useCallback(
		idToRemove => {
			setHistoryMap(prevMap => {
				const updatedCategoryHistory = (prevMap[category] || []).filter(
					entry => entry.id !== idToRemove
				);
				const updatedMap = {
					...prevMap,
					[category]: updatedCategoryHistory,
				};
				localStorage.setItem(storageKey, JSON.stringify(updatedMap));
				return updatedMap;
			});
		},
		[category, storageKey]
	);

	const clearHistory = useCallback(() => {
		setHistoryMap(prevMap => {
			const updatedMap = {
				...prevMap,
				[category]: [],
			};
			localStorage.setItem(storageKey, JSON.stringify(updatedMap));
			return updatedMap;
		});
	}, [category, storageKey]);

	return {
		history: historyMap[category] || [],
		addSearch,
		clearHistory,
		removeSearch,
		getSearchSearchById,
	};
};

export default useSearchHistory;
